import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';

import { MatCard, MatCardContent } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';

import { FsBadgeModule } from '@firestitch/badge';
import { FsChipModule } from '@firestitch/chip';
import { FsDateModule } from '@firestitch/date';
import { ItemType } from '@firestitch/filter';
import { FsListComponent, FsListConfig, FsListModule } from '@firestitch/list';
import { FsPopoverModule } from '@firestitch/popover';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';

import { MeetingPersonData } from '@common/data/meeting-person.data';
import { MeetingPerson } from '@common/interfaces/meeting';
import { NavService, SessionService } from '@common/services';

import {
  MeetingPersonDialogComponent,
  MeetingPersonDialogData,
} from '@libs/meeting/components/meeting-person-dialog/meeting-person-dialog.component';


@Component({
  templateUrl: './meeting-persons.component.html',
  styleUrls: ['./meeting-persons.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FsBadgeModule,
    FsChipModule,
    FsDateModule,
    FsListModule,
    FsPopoverModule,
    MatCard,
    MatCardContent,
  ],
})
export class MeetingPersonsComponent implements OnInit {

  @ViewChild(FsListComponent)
  public list: FsListComponent;

  public listConfig: FsListConfig;

  private readonly _personData = inject(MeetingPersonData);
  private readonly _dialog = inject(MatDialog);
  private readonly _navService = inject(NavService);
  private readonly _sessionService = inject(SessionService);
  private readonly _destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this._navService.setTitle('Meeting People');
    this._initListConfig();
  }

  public openPerson(person: MeetingPerson): void {
    if (!person?.id) {
      return;
    }
    this._dialog
      .open<MeetingPersonDialogComponent, MeetingPersonDialogData, MeetingPerson | null>(
        MeetingPersonDialogComponent,
        {
          data: { meetingPersonId: person.id },
          width: '480px',
          autoFocus: false,
        },
      )
      .afterClosed()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this.list?.reload());
  }

  private _initListConfig(): void {
    this.listConfig = {
      sort: { value: 'name', direction: 'asc' },
      filters: [
        {
          name: 'keyword',
          type: ItemType.Keyword,
          label: 'Search',
        },
      ],
      rowActions: [
        {
          click: (row) => this._personData.delete(row),
          remove: {
            title: 'Confirm',
            template: 'Remove this person? Speakers that pointed to this person will be un-linked.',
          },
          label: 'Delete',
        },
      ],
      fetch: (query) => {
        query.environmentId = this._sessionService.environmentId();
        query.accounts = true;
        query.accountAvatars = true;
        query.projects = true;
        query.projectAvatars = true;

        return this._personData.gets(query, { key: null })
          .pipe(
            map((response: any) => ({
              data: response.meetingPersons,
              paging: response.paging,
            })),
          );
      },
    };
  }
}
